import { useEffect, useRef, useState } from 'react';

interface Props {
  id?: string;
  /** Valeur de reference (le store). Une remise a zero depuis l'exterieur vide
   *  le champ, mais jamais sous les doigts de celui qui est en train d'ecrire. */
  value: string;
  onCommit: (value: string) => void;
  placeholder: string;
  maxLength: number;
  rows: number;
  className: string;
  counterClassName: string;
}

/** Assez court pour qu'un aller-retour vers le store reste imperceptible, assez
 *  long pour qu'une frappe normale ne le declenche qu'une fois par mot. */
const COMMIT_DELAY_MS = 200;

/**
 * Zone de note qui garde la frappe en local et ne remonte au store qu'apres une
 * pause — ou des que le champ perd le focus.
 *
 * Branche directement sur redux, chaque caractere re-rendait tout le tiroir /
 * toute la page de paiement. Mesure au navigateur sur le carnet du serveur
 * (2026-08-27, telephone 390x844) : p50 49 ms, p90 201 ms, pointe 400 ms par
 * caractere pour la note du panier, contre p50 17 ms pour le « commentaire du
 * plat » de ProductModal, qui lui est deja en etat local. Le serveur ecrit
 * « table pressee » et voit les lettres arriver en retard sur son doigt.
 *
 * La sortie du champ valide toujours : sur mobile, toucher « Passer la
 * commande » fait d'abord perdre le focus au textarea, donc la note est dans le
 * store avant que le clic ne parte.
 */
export default function NoteField({
  id,
  value,
  onCommit,
  placeholder,
  maxLength,
  rows,
  className,
  counterClassName,
}: Props) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  // Le parent recree son `onCommit` a chaque rendu : la version differee doit
  // appeler la derniere, pas celle capturee au moment de la frappe.
  const commit = useRef(onCommit);
  commit.current = onCommit;

  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const schedule = (next: string) => {
    setDraft(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => commit.current(next), COMMIT_DELAY_MS);
  };

  const flush = (next: string) => {
    window.clearTimeout(timer.current);
    focused.current = false;
    commit.current(next);
  };

  return (
    <>
      <textarea
        id={id}
        className={className}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        value={draft}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={(e) => flush(e.target.value.slice(0, maxLength))}
        onChange={(e) => schedule(e.target.value.slice(0, maxLength))}
      />
      <div className={counterClassName}>{`${draft.length} / ${maxLength}`}</div>
    </>
  );
}
